import { db } from "./db.js";
import { ref } from "vue";
import { getMemberCaching, getCachedMemberRole } from "./teams.js";

// ==================== CREATE ====================

function addDocketTask(firmId, data) {
  const ref1 = db.collection(`firms/${firmId}/additions/tm-addition/trademark-tasks/`).doc();
  data.id = ref1.id;
  return ref1.set(data).catch( error => { console.log("Error in addDocketTask " + error); });
}

// ==================== READ ====================

function streamDocketing(firmId, onSnap) {
  return db.collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`)
    .orderBy("deadline")
    .onSnapshot(onSnap, (error) => { console.log(error); });
}

// Role-aware variant (mirrors streamTrademarksOfUser): firestore.rules scope
// task reads to admins/managers/owners or staff whose uid is in the task's
// assigned[] (denormalized from the trademark). Members must therefore query
// with the assigned constraint; admins/managers stream everything. Uses the
// cached role to attach instantly on repeat visits, re-checking membership in
// the background. Resolves to the unsubscribe function.
async function streamDocketingOfUser(firmId, userId, onSnap) {
  const collectionRef = db.collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`);
  const queryFor = (role) =>
    role === "admin" || role === "manager"
      ? collectionRef.orderBy("deadline")
      : collectionRef.where("assigned", "array-contains", userId).orderBy("deadline");
  const onError = (error) => { console.log(error); };

  const cachedRole = getCachedMemberRole(firmId, userId);
  if (cachedRole) {
    let unsubscribe = queryFor(cachedRole).onSnapshot(onSnap, onError);
    getMemberCaching(firmId, userId).then((member) => {
      const freshRole = (member && member.role) || "member";
      if (freshRole !== cachedRole) {
        unsubscribe();
        unsubscribe = queryFor(freshRole).onSnapshot(onSnap, onError);
      }
    });
    return () => unsubscribe();
  }

  const member = await getMemberCaching(firmId, userId);
  return queryFor((member && member.role) || "member").onSnapshot(onSnap, onError);
}

async function getDocketTasksByDeadline(firmId) {
    const tasks = ref([]);
    await db.collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`)
    .orderBy("deadline")
    .get()
    .then((querySnapshot) => {
        tasks.value = querySnapshot.docs.map((doc) => {
          return { id: doc.id, ...doc.data() };
        });
      });
    return tasks;
}

// Role-aware variant of getDocketTasksByDeadline — same scoping as
// streamDocketingOfUser (members only see tasks assigned to them). Runs with
// the cached role when available; on a rules denial (stale role) it re-checks
// membership and retries once.
async function getDocketTasksByDeadlineOfUser(firmId, userId) {
    const collectionRef = db.collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`);
    const queryFor = (role) =>
      role === "admin" || role === "manager"
        ? collectionRef.orderBy("deadline")
        : collectionRef.where("assigned", "array-contains", userId).orderBy("deadline");
    const run = async (query) => {
      const tasks = ref([]);
      await query.get().then((querySnapshot) => {
        tasks.value = querySnapshot.docs.map((doc) => {
          return { id: doc.id, ...doc.data() };
        });
      });
      return tasks;
    };

    const cachedRole = getCachedMemberRole(firmId, userId);
    if (cachedRole) {
      try {
        const result = await run(queryFor(cachedRole));
        getMemberCaching(firmId, userId); // refresh cache in the background
        return result;
      } catch (error) {
        console.log("Cached-role task query rejected, re-checking membership:", error);
      }
    }
    const member = await getMemberCaching(firmId, userId);
    return run(queryFor((member && member.role) || "member"));
}

async function getDocketTasksByTrademarkSerialNumberByDeadline(firmId, serialNumber) {
    const tasks = ref([]);
    await db
    .collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`)
    .where("serialNumber", "==", serialNumber)
    .orderBy("deadline")
    .get()
    .then((querySnaptshot) => {
      tasks.value = querySnaptshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
    return tasks;
}

async function getDocketTasksByTrademarkSerialNumberByStatus(firmId, serialNumber) {
    const tasks = ref([]);
    await db
    .collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`)
    .where("serialNumber", "==", serialNumber)
    .orderBy("status")
    .get()
    .then((querySnaptshot) => {
      tasks.value = querySnaptshot.docs.map((doc) => {
        return { id: doc.id, ...doc.data() };
      });
    });
    return tasks;
}

async function getDocketTasksByStatus(firmId) {
    const tasks = ref([]);
    db.collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`)
    .orderBy("status", "asc")
    .get()
    .then((querySnapshot) => {
        tasks.value = querySnapshot.docs.map((doc) => {
          return { id: doc.id, ...doc.data() };
        });
      });
    return tasks;
}

async function getDocketTask(firmId, taskId) {
    const task = await db.doc(`firms/${firmId}/additions/tm-addition/trademark-tasks/${taskId}`).get();
    return task.exists ? task.data() : null;
}

async function getPendingDocketTasks(firmId) {
    const tasks = ref([]);
    await db.collection(`firms/${firmId}/additions/tm-addition/trademark-tasks`)
    .where("status", "!=", "Completed")
    .get()
    .then((querySnapshot) => {
        tasks.value = querySnapshot.docs.map((doc) => {
          return { id: doc.id, ...doc.data() };
        });
      });
    return tasks;
}

// ==================== UPDATE ====================

async function updateDocketTask(firmId, taskId, data, transaction = null) {
  const ref = db.doc(`firms/${firmId}/additions/tm-addition/trademark-tasks/${taskId}`);
  if (transaction) {
    transaction.update(ref, data);
  } else {
    await ref.update(data);
  }
}

// ==================== NO DELETE ALLOWED ====================

export {
  addDocketTask,
  getDocketTask,
  getDocketTasksByDeadline,
  getDocketTasksByDeadlineOfUser,
  getDocketTasksByStatus,
  getDocketTasksByTrademarkSerialNumberByDeadline,
  getDocketTasksByTrademarkSerialNumberByStatus,
  getPendingDocketTasks,
  streamDocketing,
  streamDocketingOfUser,
  updateDocketTask,
};
